import LoggerService from "@bfx/logger";
import {
  LOG_DATE_TIME_FORMAT,
  MESSAGE_LOG_PREFIX,
} from "shared/broker-client-services/worker/CubiqLogger.constants";
import { CubiqLoggerConfig } from "shared/broker-client-services/worker/CubiqLoggerConfig";
import { CubiqMessageLogger } from "shared/broker-client-services/worker/CubiqMessageLogger";
import { CubiqRequestLogger } from "shared/broker-client-services/worker/CubiqRequestLogger";
import { CubiqResponseLogger } from "shared/broker-client-services/worker/CubiqResponseLogger";
import {
  ConcreteEncodable,
  Encodable,
  moment,
  ServiceResponseImpl,
} from "shared/cubiq-model-multi-asset";
const logger = LoggerService.for(__dirname);

/**
 * Wrapper around all cubiq logging functionality - contains instances of logging classes rpesonsible for
 * logging all Cubiq messages, requests and responses.
 */
export class CubiqLogger {
  private config: CubiqLoggerConfig;

  private messageLogger: CubiqMessageLogger;
  private requestLogger: CubiqRequestLogger;
  private responseLogger: CubiqResponseLogger;

  constructor() {
    this.config = new CubiqLoggerConfig();
    this.messageLogger = new CubiqMessageLogger(this.config);
    this.requestLogger = new CubiqRequestLogger(this.config);
    this.responseLogger = new CubiqResponseLogger(this.config);

    const now = moment();
    logger.out.info(
      `${MESSAGE_LOG_PREFIX} - Starting session logging at ${now.format(LOG_DATE_TIME_FORMAT)}`,
    );
  }

  /**
   * Configure with a simple object of properties whose names match private config member properties
   * Not really useful at the moment - easiest to change the logging class - but could be used to change config at runtime
   * e.g. {showShortMsgId:false, showErrorArgs:true }
   * @param config
   */
  public configure(config: { [key: string]: any }) {
    // For moment, let this config implicitly update all sub loggers
    this.config.configure(config);
  }

  /**
   * Configure request logging with a simple object of - requestType=> boolean properties
   * @param config
   */
  public configureRequest(config: { [key: string]: boolean }) {
    this.config.configureRequest(config);
  }
  /**
   * Configure request logging with a simple object of - requestType=> boolean properties (we can't necessarily
   * tell the responseType from the requestType)
   * @param config
   */
  public configureResponse(config: { [key: string]: boolean }) {
    this.config.configureResponse(config);
  }
  /**
   * Configure request logging with a simple object of - messagetype=> boolean properties
   * @param config
   */
  public configureMessage(config: { [key: string]: boolean }) {
    this.config.configureMessage(config);
  }

  /**
   * Process an incoming message - it will either be an ansynchronous message or a ServiceResponse
   * from a previously-made request
   * @param messageType
   * @param payload
   */
  public processMessage(messageType: string, payload: Encodable) {
    try {
      if (messageType === "ServiceResponseImpl") {
        this.responseLogger.processResponse(payload as ServiceResponseImpl);
      } else {
        this.messageLogger.processMessage(messageType, payload);
      }
    } catch (error) {
      logger.out.error("cub-m - Error in Cubiq message logging", error);
    }
  }

  /**
   * Called after message has been processed/dispatched to any interested listeners
   * @param message
   * @param listeners
   */
  public postProcessMessage(message: ConcreteEncodable, listeners: ((event: Encodable) => void)[]) {
    if (!listeners || listeners.length == 0) {
      this.messageLogger.logUnhandled("Message", message.encodableType);
    }
  }

  /**
   * Process a single Cubiq request
   * @param request
   */
  public processRequest(request: Encodable) {
    try {
      this.requestLogger.processRequest(request);
    } catch (error) {
      logger.out.error("cub-m - Error in Cubiq request logging", error);
    }
  }
}
